import { useMutation, useQueryClient } from '@tanstack/react-query';
import ChatService from '../services/chat.service';
import { FileItem } from '../types/File/File.types';

export interface MoveDocumentPayload {
    documentId: string;
    organizationId: string;
    destinationFolderId?: string;
}

export interface MoveDocumentOptions {
    onSuccess?: (data: unknown, variables: MoveDocumentPayload) => void;
    onError?: (error: Error, variables: MoveDocumentPayload) => void;
}

/**
 * Enhanced hook to move a document to a folder with improved error handling and validation.
 *
 * @param options - Optional success and error callbacks
 * @returns An object containing the mutation state and the `mutateAsync` function to trigger the move operation.
 */
export const useMoveDocumentToFolder = (options?: MoveDocumentOptions) => {
    const queryClient = useQueryClient();
    const chatService = new ChatService();

    return useMutation({
        mutationFn: async (payload: MoveDocumentPayload) => {
            // Validate payload
            if (!payload.documentId) {
                throw new Error('Document ID is required');
            }
            if (!payload.organizationId) {
                throw new Error('Organization ID is required');
            }

            return chatService.moveDocumentToFolder(
                payload.documentId,
                payload.organizationId,
                payload.destinationFolderId,
            );
        },
        onSuccess: (data, variables) => {
            // Invalidate relevant queries to refresh the UI
            queryClient.invalidateQueries({ queryKey: ['documents'] });
            queryClient.invalidateQueries({ queryKey: ['folders'] });

            // Call custom success callback if provided
            options?.onSuccess?.(data, variables);
        },
        onError: (error, variables) => {
            console.error('Move operation failed:', error);

            // Call custom error callback if provided
            options?.onError?.(error as Error, variables);
        },
    });
};

/**
 * Hook to move multiple documents to a folder in batch.
 *
 * @param options - Optional success and error callbacks
 * @returns An object containing the mutation state for batch operations
 */
export const useBatchMoveDocuments = (options?: BatchMoveDocumentOptions) => {
    const queryClient = useQueryClient();
    const chatService = new ChatService();

    return useMutation({
        mutationFn: async (payload: BatchMoveDocumentPayload) => {
            const promises = payload.documentIds.map((documentId) =>
                chatService.moveDocumentToFolder(documentId, payload.organizationId, payload.destinationFolderId),
            );

            return Promise.allSettled(promises);
        },
        onSuccess: (data, variables) => {
            // Check for any failed operations
            const failed = data.filter((result) => result.status === 'rejected').length;
            const succeeded = data.filter((result) => result.status === 'fulfilled').length;

            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['documents'] });
            queryClient.invalidateQueries({ queryKey: ['folders'] });

            // Call custom success callback with results summary
            options?.onSuccess?.({ succeeded, failed, total: data.length }, variables);
        },
        onError: (error, variables) => {
            console.error('Batch move operation failed:', error);
            options?.onError?.(error as Error, variables);
        },
    });
};

/**
 * Utility function to validate if a move operation is allowed
 *
 * @param item - The item to be moved
 * @param targetFolderId - The destination folder ID
 * @param currentFolderId - The current folder ID
 * @returns Object with validation result and error message if invalid
 */
export const validateMoveOperation = (
    item: FileItem,
    targetFolderId: string | undefined,
    currentFolderId: string,
): { isValid: boolean; errorMessage?: string } => {
    // Cannot move to the same location
    if (targetFolderId === currentFolderId) {
        return {
            isValid: false,
            errorMessage: 'Cannot move item to its current location',
        };
    }

    // If moving a folder, prevent recursive moves
    if (item.type === 'folder') {
        // Cannot move folder into itself
        if (targetFolderId === item.id) {
            return {
                isValid: false,
                errorMessage: 'Cannot move folder into itself',
            };
        }

        // Cannot move folder into its descendants
        if (targetFolderId && targetFolderId.startsWith(`${item.id}/`)) {
            return {
                isValid: false,
                errorMessage: 'Cannot move folder into its descendant',
            };
        }
    }

    return { isValid: true };
};

export interface BatchMoveDocumentPayload {
    documentIds: string[];
    organizationId: string;
    destinationFolderId?: string;
}

export interface BatchMoveDocumentOptions {
    onSuccess?: (data: unknown, variables: BatchMoveDocumentPayload) => void;
    onError?: (error: Error, variables: BatchMoveDocumentPayload) => void;
}
